import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { collection, getDocs, getDocsFromCache } from 'firebase/firestore';
import { db } from '../lib/firebase';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { debug } from '../utils/debug';

interface LoyaltyTier {
  name: string;
  min_value: number;
  max_value: number | null;
}

interface LoyaltyCalculationRules {
  method: 'days' | 'cabinTypeMultiplier' | 'manual' | 'sailings';
  pointsPerDay?: number;
  cabinTypeMultipliers?: { [cabinType: string]: number };
  forceManualInput?: boolean;
  /** Secondary sailing-count track for programs like Princess Captain's Circle.
   *  When set, the UI also counts in-app completed sailings and picks the better tier. */
  secondaryTrack?: 'sailings';
  secondaryTiers?: LoyaltyTier[];
  /** Links multiple cruise lines together into a single program entity (e.g. 'pointsChoice') */
  loyaltyCombinedGroup?: string;
  supportsGrandfathering?: boolean;
  cycleDurationYears?: number;
  currentCycleEnd?: string;
}

export interface ShipMetadata {
  locationUrl?: string;
  infoUrl?: string;
  deckplanUrl?: string;
  yearBuilt?: string;
  lastRefurbishment?: string;
  shipClass?: string;
  normalizedClass?: string;
  grossTonnage?: string;
  decks?: string;
  length?: string;
  width?: string;
  speed?: string;
  passengers?: string;
  crew?: string;
  cabin?: string;
  formerNames?: string;
  builder?: string;
  flagState?: string;
}

export interface CruiseMetadata {
  id?: string;
  name: string;
  ships: string[];
  retiredShips?: string[];
  majorline: boolean;
  loyalty?: boolean;
  loyaltyTiers?: LoyaltyTier[];
  loyaltyUnit?: string;
  loyaltyProgramName?: string;
  shipImages?: { [shipName: string]: string };
  shipMetadata?: { [shipName: string]: ShipMetadata };
  tierMatches?: LoyaltyTierMatch[];
  loyaltyCalculationRules?: LoyaltyCalculationRules;
  manage_url?: string;
}

interface LoyaltyTierMatch {
  fromTierName: string;
  matches: {
    toCruiseLine: string;
    toTierName: string;
  }[];
}

interface CruiseMetadataContextType {
  cruiseLines: CruiseMetadata[];
  loading: boolean;
  refreshCruiseMetadata: () => Promise<void>;
  getShipsForCruiseLine: (cruiseLine: string) => { active: string[]; retired: string[]; };
  getLoyaltyCruiseLines: () => CruiseMetadata[];
  getLoyaltyTiersForCruiseLine: (cruiseLine: string) => { tiers: LoyaltyTier[], unit: string, programName?: string } | null;
  getLoyaltyTierName: (cruiseLine: string, value: number, grandfatheredTier?: string) => string | null;
  getTierMinValueByName: (cruiseLine: string, tierName: string) => number | null;
  /** Returns the tier name for a program's secondary sailing-count track, or null if not configured. */
  getSecondaryTrackTier: (cruiseLine: string, sailingCount: number) => string | null;
  /** Returns lines that belong to a specific loyalty group (e.g. 'pointsChoice') */
  getCruiseLinesInLoyaltyGroup: (groupName: string) => CruiseMetadata[];
  getShipImageUrl: (cruiseLine: string, shipName: string) => string | null;
  getLoyaltyMatches: (fromCruiseLine: string, fromTierName: string) => { toCruiseLine: string; toTierName: string }[];
  getLoyaltyCalculationRules: (cruiseLine: string) => LoyaltyCalculationRules | null;
  getManageUrl: (cruiseLine: string) => string | null;
  getShipLocationUrl: (cruiseLine: string, shipName: string) => string | null;
  getShipInfoUrl: (cruiseLine: string, shipName: string) => string | null;
  getShipDeckplanUrl: (cruiseLine: string, shipName: string) => string | null;
  getShipMetadata: (cruiseLine: string, shipName: string) => ShipMetadata | null;
}

const CruiseMetadataContext = createContext<CruiseMetadataContextType | null>(null);

export const useCruiseMetadata = () => {
  const context = useContext(CruiseMetadataContext);
  if (!context) {
    throw new Error('useCruiseMetadata must be used within a CruiseMetadataProvider');
  }
  return context;
};

interface CruiseMetadataProviderProps {
  children: ReactNode;
}

export const CruiseMetadataProvider = ({ children }: CruiseMetadataProviderProps) => {
  const [cruiseLines, setCruiseLines] = useState<CruiseMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  const fetchCruiseMetadata = async () => {
    // If no user is authenticated or email is not verified, set empty data and return
    if (!currentUser || !currentUser.emailVerified) {
      setCruiseLines([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const metadataRef = collection(db, 'cruiseMetadata');
      let cacheFound = false;
      let hasSetLoading = false;

      // Phase 1: Try cache immediately (fastest path - ~50-200ms)
      try {
        const cachedSnapshot = await getDocsFromCache(metadataRef);
        if (cachedSnapshot.docs.length > 0) {
          const metadata = cachedSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().name,
            ships: doc.data().ships,
            retiredShips: doc.data().retiredShips || [],
            majorline: doc.data().majorline || false,
            loyalty: doc.data().loyalty || false,
            loyaltyTiers: doc.data().loyaltyTiers || [],
            loyaltyUnit: doc.data().loyaltyUnit || 'points',
            loyaltyProgramName: doc.data().loyaltyProgramName || undefined,
            shipImages: doc.data().shipImages || {},
            shipMetadata: doc.data().shipMetadata || {},
            tierMatches: doc.data().tierMatches || [],
            loyaltyCalculationRules: doc.data().loyaltyCalculationRules || undefined,
            manage_url: doc.data().manage_url || undefined
          }));

          // Sort cruise lines: major lines first, then specialty lines, both in alphabetical order
          const sortedMetadata = [
            ...metadata
              .filter(line => line.majorline)
              .sort((a, b) => a.name.localeCompare(b.name)),
            ...metadata
              .filter(line => !line.majorline)
              .sort((a, b) => a.name.localeCompare(b.name))
          ];

          setCruiseLines(sortedMetadata);
          setLoading(false);
          hasSetLoading = true;
          cacheFound = true;
          debug.debug('[CruiseMetadata] Loaded from cache immediately (~50-200ms)');
        }
      } catch {
        // No cache available yet - that's okay, will fetch from server
        debug.debug('[CruiseMetadata] No cache available for immediate load');
      }

      // Phase 2: Always fetch from server in background to get latest data
      // Safety timeout: if we have no cache and the server doesn't respond within
      // METADATA_TIMEOUT_MS, unblock loading so the UI isn't stuck forever.
      // Only set on cache-miss path since cacheFound already called setLoading(false).
      const METADATA_TIMEOUT_MS = 5000;
      let metadataTimeoutId: ReturnType<typeof setTimeout> | null = null;
      if (!cacheFound) {
        metadataTimeoutId = setTimeout(() => {
          if (!hasSetLoading) {
            debug.warn('[CruiseMetadata] Server fetch timeout — unblocking with empty data');
            setLoading(false);
            hasSetLoading = true;
          }
        }, METADATA_TIMEOUT_MS);
      }

      getDocs(metadataRef).then((snapshot) => {
        if (metadataTimeoutId) {
          clearTimeout(metadataTimeoutId);
          metadataTimeoutId = null;
        }
        const metadata = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          ships: doc.data().ships,
          retiredShips: doc.data().retiredShips || [],
          majorline: doc.data().majorline || false,
          loyalty: doc.data().loyalty || false,
          loyaltyTiers: doc.data().loyaltyTiers || [],
          loyaltyUnit: doc.data().loyaltyUnit || 'points',
          loyaltyProgramName: doc.data().loyaltyProgramName || undefined,
          shipImages: doc.data().shipImages || {},
          shipMetadata: doc.data().shipMetadata || {},
          tierMatches: doc.data().tierMatches || [],
          loyaltyCalculationRules: doc.data().loyaltyCalculationRules || undefined,
          manage_url: doc.data().manage_url || undefined
        }));

        // Sort cruise lines: major lines first, then specialty lines, both in alphabetical order
        const sortedMetadata = [
          ...metadata
            .filter(line => line.majorline)
            .sort((a, b) => a.name.localeCompare(b.name)),
          ...metadata
            .filter(line => !line.majorline)
            .sort((a, b) => a.name.localeCompare(b.name))
        ];

        setCruiseLines(sortedMetadata);
        
        if (!hasSetLoading) {
          setLoading(false);
          debug.debug('[CruiseMetadata] Loaded from server');
        } else {
          debug.debug('[CruiseMetadata] Silent refresh completed from server');
        }
      }).catch((error) => {
        if (metadataTimeoutId) {
          clearTimeout(metadataTimeoutId);
          metadataTimeoutId = null;
        }
        console.error('Error fetching cruise metadata:', error);
        if (!hasSetLoading) {
          setLoading(false);
        }
        // Only show error toast if we don't have cached data
        if (!cacheFound) {
          toast.error('Failed to load cruise lines data');
        }
      });

    } catch (error) {
      console.error('Error in fetchCruiseMetadata:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCruiseMetadata();
  }, [currentUser]);

  const refreshCruiseMetadata = async () => {
    await fetchCruiseMetadata();
  };

  const getShipsForCruiseLine = (cruiseLine: string): { active: string[]; retired: string[]; } => {
    const cruiseLineData = cruiseLines.find(cl => cl.name === cruiseLine);
    if (!cruiseLineData) {
      return { active: [], retired: [] };
    }
    
    const activeShips = (cruiseLineData.ships || []).sort();
    const retiredShips = (cruiseLineData.retiredShips || []).sort();
    
    return { active: activeShips, retired: retiredShips };
  };

  const getLoyaltyCruiseLines = (): CruiseMetadata[] => {
    return cruiseLines
      .filter(cl => cl.loyalty === true)
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const getLoyaltyTiersForCruiseLine = (cruiseLine: string): { tiers: LoyaltyTier[], unit: string, programName?: string } | null => {
    const cruiseLineData = cruiseLines.find(cl => cl.name === cruiseLine);
    if (!cruiseLineData || !cruiseLineData.loyaltyTiers || cruiseLineData.loyaltyTiers.length === 0) {
      return null;
    }
    return {
      tiers: cruiseLineData.loyaltyTiers,
      unit: cruiseLineData.loyaltyUnit || 'points',
      programName: cruiseLineData.loyaltyProgramName
    };
  };

  const getLoyaltyTierName = (cruiseLine: string, value: number, grandfatheredTier?: string): string | null => {
    const loyaltyData = getLoyaltyTiersForCruiseLine(cruiseLine);
    if (!loyaltyData) return grandfatheredTier || null;

    const calculatedTier = loyaltyData.tiers.find(tier => {
      const meetsMin = value >= tier.min_value;
      const meetsMax = tier.max_value === null || value <= tier.max_value;
      return meetsMin && meetsMax;
    })?.name || null;

    if (!grandfatheredTier) return calculatedTier;
    if (!calculatedTier) return grandfatheredTier;

    const calcMin = loyaltyData.tiers.find(t => t.name.toLowerCase() === calculatedTier.toLowerCase())?.min_value ?? 0;
    const grandMin = loyaltyData.tiers.find(t => t.name.toLowerCase() === grandfatheredTier.toLowerCase())?.min_value ?? 0;

    return grandMin >= calcMin ? grandfatheredTier : calculatedTier;
  };

  const getSecondaryTrackTier = (cruiseLine: string, sailingCount: number): string | null => {
    const cruiseLineData = cruiseLines.find(cl => cl.name === cruiseLine);
    const secondaryTiers = cruiseLineData?.loyaltyCalculationRules?.secondaryTiers;
    if (!secondaryTiers || secondaryTiers.length === 0) return null;

    const tier = secondaryTiers.find(t => {
      const meetsMin = sailingCount >= t.min_value;
      const meetsMax = t.max_value === null || sailingCount <= t.max_value;
      return meetsMin && meetsMax;
    });
    return tier ? tier.name : null;
  };

  const getCruiseLinesInLoyaltyGroup = (groupName: string): CruiseMetadata[] => {
    return cruiseLines.filter(cl => 
      cl.loyaltyCalculationRules?.loyaltyCombinedGroup === groupName
    );
  };

  const getTierMinValueByName = (cruiseLine: string, tierName: string): number | null => {
    const loyaltyData = getLoyaltyTiersForCruiseLine(cruiseLine);
    if (!loyaltyData) return null;

    const tier = loyaltyData.tiers.find(tier => tier.name === tierName);
    return tier ? tier.min_value : null;
  };

  const getShipImageUrl = (cruiseLine: string, shipName: string): string | null => {
    const cruiseLineData = cruiseLines.find(cl => cl.name === cruiseLine);
    if (!cruiseLineData || !cruiseLineData.shipImages) {
      debug.log(`[ShipImage] No metadata found for cruise line: ${cruiseLine}`);
      return null;
    }
    
    // Tier 1: Try to get specific ship image with case-insensitive matching
    const shipImages = cruiseLineData.shipImages;
    const shipImageKey = Object.keys(shipImages).find(
      key => key.toLowerCase() === shipName.toLowerCase()
    );
    
    if (shipImageKey) {
      const specificImage = shipImages[shipImageKey];
      debug.log(`[ShipImage] Found specific image for ${cruiseLine} - ${shipName} (matched key: ${shipImageKey}): ${specificImage}`);
      return specificImage;
    }
    
    // Tier 2: Try to get default cruise line image (using "Default" as key)
    const defaultImage = shipImages["Default"];
    if (defaultImage) {
      debug.log(`[ShipImage] Using default image for ${cruiseLine} (no specific image for ${shipName}): ${defaultImage}`);
      return defaultImage;
    }
    
    // Tier 3: No image available, return null (will fall back to gradient)
    debug.log(`[ShipImage] No image available for ${cruiseLine} - ${shipName}, falling back to gradient`);
    return null;
  };

  const getLoyaltyMatches = (fromCruiseLine: string, fromTierName: string): { toCruiseLine: string; toTierName: string }[] => {
    const cruiseLineData = cruiseLines.find(cl => cl.name === fromCruiseLine);
    if (!cruiseLineData || !cruiseLineData.tierMatches) return [];

    const tierMatch = cruiseLineData.tierMatches.find(tier => tier.fromTierName === fromTierName);
    return tierMatch ? tierMatch.matches : [];
  };

  const getLoyaltyCalculationRules = (cruiseLine: string): LoyaltyCalculationRules | null => {
    const cruiseLineData = cruiseLines.find(cl => cl.name === cruiseLine);
    return cruiseLineData?.loyaltyCalculationRules || null;
  };

  const getManageUrl = (cruiseLine: string): string | null => {
    const cruiseLineData = cruiseLines.find(cl => cl.name === cruiseLine);
    return cruiseLineData?.manage_url || null;
  };

  const getShipLocationUrl = (cruiseLine: string, shipName: string): string | null => {
    const cruiseLineData = cruiseLines.find(cl => cl.name === cruiseLine);
    if (!cruiseLineData || !cruiseLineData.shipMetadata) {
      return null;
    }
    
    // Find ship metadata key with case-insensitive matching
    const shipMetadata = cruiseLineData.shipMetadata;
    const shipMetadataKey = Object.keys(shipMetadata).find(
      key => key.toLowerCase() === shipName.toLowerCase()
    );
    
    if (shipMetadataKey) {
      return shipMetadata[shipMetadataKey]?.locationUrl || null;
    }
    
    return null;
  };

  const getShipInfoUrl = (cruiseLine: string, shipName: string): string | null => {
    const cruiseLineData = cruiseLines.find(cl => cl.name === cruiseLine);
    if (!cruiseLineData || !cruiseLineData.shipMetadata) {
      return null;
    }
    
    // Find ship metadata key with case-insensitive matching
    const shipMetadata = cruiseLineData.shipMetadata;
    const shipMetadataKey = Object.keys(shipMetadata).find(
      key => key.toLowerCase() === shipName.toLowerCase()
    );
    
    if (shipMetadataKey) {
      return shipMetadata[shipMetadataKey]?.infoUrl || null;
    }
    
    return null;
  };

  const getShipDeckplanUrl = (cruiseLine: string, shipName: string): string | null => {
    const cruiseLineData = cruiseLines.find(cl => cl.name === cruiseLine);
    if (!cruiseLineData || !cruiseLineData.shipMetadata) {
      return null;
    }
    
    // Find ship metadata key with case-insensitive matching
    const shipMetadata = cruiseLineData.shipMetadata;
    const shipMetadataKey = Object.keys(shipMetadata).find(
      key => key.toLowerCase() === shipName.toLowerCase()
    );
    
    if (shipMetadataKey) {
      return shipMetadata[shipMetadataKey]?.deckplanUrl || null;
    }
    
    return null;
  };

  const getShipMetadata = (cruiseLine: string, shipName: string): ShipMetadata | null => {
    const cruiseLineData = cruiseLines.find(cl => cl.name === cruiseLine);
    if (!cruiseLineData || !cruiseLineData.shipMetadata) {
      return null;
    }
    
    // Find ship metadata key with case-insensitive matching
    const shipMetadata = cruiseLineData.shipMetadata;
    const shipMetadataKey = Object.keys(shipMetadata).find(
      key => key.toLowerCase() === shipName.toLowerCase()
    );
    
    if (shipMetadataKey) {
      return shipMetadata[shipMetadataKey] || null;
    }
    
    return null;
  };

  const value = {
    cruiseLines,
    loading,
    refreshCruiseMetadata,
    getShipsForCruiseLine,
    getLoyaltyCruiseLines,
    getLoyaltyTiersForCruiseLine,
    getLoyaltyTierName,
    getTierMinValueByName,
    getSecondaryTrackTier,
    getCruiseLinesInLoyaltyGroup,
    getShipImageUrl,
    getLoyaltyMatches,
    getLoyaltyCalculationRules,
    getManageUrl,
    getShipLocationUrl,
    getShipInfoUrl,
    getShipDeckplanUrl,
    getShipMetadata
  };

  return (
    <CruiseMetadataContext.Provider value={value}>
      {children}
    </CruiseMetadataContext.Provider>
  );
};